import {
    Fade,
    Grid,
    Grow,
    Paper,
    styled,
    Theme,
    Typography,
    useTheme,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import LayersIcon from "@mui/icons-material/Layers";
import PlaylistAddSharpIcon from "@mui/icons-material/PlaylistAddSharp";
import SortIcon from "@mui/icons-material/Sort";
import { ToggleButton, ToggleButtonGroup } from "@mui/material";
import { Observer } from "mobx-react";
import Classifier from "models/ClassifierModel";
import React from "react";
import { DropTargetMonitor, useDrop } from "react-dnd";
import { CombinatorRuleType } from "../../../../../../models/RuleCombinator";
import hexToRGB from "../../../../../../utils/hexToRGB";
import { RuleBuildStore } from "../../RuleBuildStore";
import { OnDropParams, RuleTypes } from "../MainRuleBuilder";
import { IAcxClassifierRuleItem } from "./AcxClassifierRuleItem";

const DropTargetDiv = styled("div")(
    ({ isActive, theme }: { isActive: boolean; theme: Theme }) => ({
        width: "100%",
        height: "285px",
        overflow: "auto",
        overscrollBehavior: "contain",
        backgroundColor: isActive
            ? hexToRGB(theme.palette.secondary.main, 0.15)
            : "inherit",
        borderRadius: theme.shape.borderRadius,
    }),
);

const HeaderGrid = styled(Grid)(({ theme }) => ({
    paddingLeft: "8px",
    height: "56px",
    borderRadius: theme.spacing(0.5),
    boxShadow: "0 0 3px 0 rgba(0,0,0,0.05), 0 1px 1px 0 rgba(0,0,0,0.05)",
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.white.main,
    fontWeight: "bold",
}));

const StyledToggleButton = styled(ToggleButton)(({ theme }) => ({
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    userSelect: "none",
    height: "32px",
    opacity: 0.35,
    backgroundColor: theme.palette.background.default,
    color: theme.palette.black.contrastText,
    fontFamily: theme.typography.fontFamily,
    fontWeight: theme.typography.fontWeightBold as any,
    "&$selected": {
        // increase the specificity for the pseudo class
        //"#4DB6AC"
        // color: "#4DB6AC"
        opacity: 1,
        backgroundColor: theme.palette.white.main,
        color: theme.palette.success.main,
    },
}));

interface IAcxRuleDropTarget {
    store: RuleBuildStore;
    title: RuleTypes;
    enableDnd?: true;
    itemModel?: Classifier[];
    onDrop?: (item: OnDropParams, monitor: DropTargetMonitor) => void;
    onRemove?: (item: IAcxClassifierRuleItem) => void;
    accept: symbol[];
}

const AcxRuleDropTarget = (props: IAcxRuleDropTarget) => {
    const theme = useTheme();
    const [{ isOver, canDrop }, drop] = useDrop({
        accept: props.accept,
        drop: (item, monitor) =>
            props.onDrop?.(
                { ...item, ruleType: props.title } as OnDropParams,
                monitor,
            ),
        collect: (monitor) => ({
            isOver: monitor.isOver(),
            canDrop: monitor.canDrop(),
        }),
    });

    const isActive = isOver && canDrop;

    return (
        <Observer>
            {() => (
                <Paper elevation={1} ref={drop}>
                    <DropTargetDiv isActive={isActive} theme={theme}>
                        <div
                            style={{
                                height: "48px",
                                marginBottom: "5px",
                                position: "sticky",
                                top: 0,
                                zIndex: 2,
                                borderRadius: "4px",
                            }}
                        >
                            <Fade
                                in={
                                    props.store.ruleListForType(props.title)
                                        .length > 0
                                }
                                timeout={600}
                            >
                                <HeaderGrid
                                    container
                                    wrap="nowrap"
                                    alignItems="center"
                                >
                                    <Grid item>
                                        <HeaderIconForRuleType
                                            ruleType={props.title}
                                        />
                                    </Grid>

                                    <Grid
                                        style={{
                                            marginLeft: "24px",
                                            userSelect: "none",
                                        }}
                                        item
                                    >
                                        {props.title ?? ""} Rules
                                    </Grid>

                                    <Grid item xs></Grid>

                                    <Grid item>
                                        {props.store.ruleListForType(
                                            props.title,
                                        ).length > 1 &&
                                            props.title !== "Stratify" && (
                                                <Grow in={true} timeout={600}>
                                                    <ToggleButtonGroup
                                                        style={{
                                                            marginRight: "8px",
                                                        }}
                                                        value={props.store.getCombinatorForRuleType(
                                                            props.title,
                                                        )}
                                                        exclusive
                                                        onChange={(
                                                            event,
                                                            value,
                                                        ) => {
                                                            if (
                                                                props.title !==
                                                                "Ranking"
                                                            ) {
                                                                props.store.setCombinatorForRuleType(
                                                                    props.title,
                                                                    value,
                                                                );
                                                            }
                                                        }}
                                                        aria-label="rule combinator"
                                                    >
                                                        <StyledToggleButton
                                                            value={
                                                                CombinatorRuleType.And
                                                            }
                                                            aria-label="and combinator"
                                                        >
                                                            AND
                                                        </StyledToggleButton>
                                                        {props.title !==
                                                            "Ranking" && (
                                                            <StyledToggleButton
                                                                value={
                                                                    CombinatorRuleType.Or
                                                                }
                                                                aria-label="or combinator"
                                                            >
                                                                OR
                                                            </StyledToggleButton>
                                                        )}
                                                    </ToggleButtonGroup>
                                                </Grow>
                                            )}
                                    </Grid>
                                </HeaderGrid>
                            </Fade>
                        </div>

                        {props.store.ruleListForType(props.title).length > 0 ? (
                            props.store
                                .ruleListForType(props.title)
                                .map((val, idx) =>
                                    props.store.ruleContentForType(
                                        val,
                                        props.title,
                                    ),
                                )
                        ) : (
                            <EmptyDropTarget
                                isActive={isActive}
                                title={props.title}
                            />
                        )}
                    </DropTargetDiv>
                </Paper>
            )}
        </Observer>
    );
};

const HeaderIconForRuleType = ({ ruleType }: { ruleType: RuleTypes }) => {
    switch (ruleType) {
        case "Stratify":
            return <LayersIcon style={{ color: "white" }} />;

        case "Ranking":
            return <SortIcon style={{ color: "white" }} />;

        case "Filter":
            return <FilterListIcon style={{ color: "white" }} />;
        default:
            throw new Error(`Unknown rule type ${ruleType}`);
    }
};

const EmptyDropTarget = (props: { title: string; isActive: boolean }) => {
    const theme = useTheme();
    return (
        <>
            <Grow in={true} timeout={600}>
                <Grid
                    sx={{
                        flexGrow: 1,
                        height: "calc(100% - 55px)",
                        borderRadius: 1,
                    }}
                    container
                    justifyContent="center"
                    alignItems={"center"}
                    direction={"column"}
                >
                    <Grid item xs={2}>
                        <PlaylistAddSharpIcon
                            sx={{
                                width: "64px",
                                height: "64px",
                                opacity: props.isActive ? 0.5 : 1,
                                color: props.isActive
                                    ? theme.palette.purple.main
                                    : "inherit",
                            }}
                            color={"disabled"}
                            fontSize={"large"}
                        />
                    </Grid>

                    <Grid item>
                        <Typography
                            sx={{
                                height: "48px",
                                maxWidth: "319px",
                                fontWeight: "bold",
                                fontFamily: theme.typography.fontFamily,
                                fontSize: "16px",
                                letterSpacing: 0,
                                lineHeight: "24px",
                                textAlign: "center",
                                userSelect: "none",
                                opacity: props.isActive ? 0.5 : 1,
                                color: props.isActive
                                    ? theme.palette.purple.main
                                    : "rgb(31,31,31)",
                                borderRadius: 1,
                            }}
                        >
                            Drag classifiers here to build{" "}
                            <span
                                style={{
                                    color: props.isActive
                                        ? theme.palette.info.main
                                        : theme.palette.info.dark,
                                }}
                            >
                                {props.title}
                            </span>{" "}
                            rules
                        </Typography>
                    </Grid>
                </Grid>
            </Grow>
        </>
    );
};

export default AcxRuleDropTarget;
